<template>
  <!-- Explore "Weather" deep-dive: store signature + per-zone signal atoms.
       DATA VIEW — not gated; renders even a mild/empty signature honestly.
       Data-driven from /weather_insight/ (signature + zones parts). -->
  <div class="wx-card">
    <div class="wx-sig">
      <div class="wx-sig-icon"><i class="bi bi-cloud-sun"></i></div>
      <div class="wx-sig-body">
        <div class="wx-sig-headline">{{ headline }}</div>
        <div v-if="drivers.length" class="wx-drivers">
          <span v-for="d in drivers" :key="d.var" class="wx-driver" :class="d.dir">
            <i class="bi" :class="varIcon(d.var)"></i>
            {{ driverLabel(d) }} <b>{{ signedPct(d) }}</b>
          </span>
        </div>
        <div class="wx-sig-note">{{ note }}</div>
      </div>
    </div>

    <div class="wx-divider"></div>

    <div class="wx-zones-head">
      <span>{{ $t('weather.store.byZone') }}</span>
      <span class="wx-legend">
        <i class="bi bi-star-fill"></i> {{ $t('weather.store.confirmed') }}
        <i class="bi bi-eye ms-2"></i> {{ $t('weather.store.early') }}
      </span>
    </div>

    <ul v-if="zones.length" class="wx-list">
      <li v-for="z in zones" :key="z.zone + z.var" class="wx-row" :class="{ muted: !z.band }">
        <div class="wx-row-icon" :class="z.dir"><i class="bi" :class="varIcon(z.var)"></i></div>
        <div class="wx-row-main">
          <div class="wx-row-top">
            <span class="wx-zone">{{ z.zone }}</span>
            <span v-if="z.kind === 'anticipatory'" class="wx-tag antic">
              <i class="bi bi-clock-history"></i> {{ $t('weather.store.anticipatory') }}
            </span>
          </div>
          <div class="wx-phrase">{{ zoneShortText(z) }}</div>
        </div>
        <div class="wx-row-right">
          <span class="wx-effect" :class="z.dir">{{ signedPct(z) }}</span>
          <span class="wx-band" :class="z.band || 'none'" :title="bandTip(z.band)">
            <i class="bi" :class="z.band === 'established' ? 'bi-star-fill' : 'bi-eye'"></i>
            {{ bandLabel(z.band) }}
          </span>
        </div>
      </li>
    </ul>
    <div v-else class="wx-empty">
      <i class="bi bi-check2-circle"></i>
      {{ $t('weather.store.empty') }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  varIcon, driverLabel, signedPct, zoneShortText, bandLabel, bandTip, signaturePhrase,
} from '@/weather/weatherPresentation'

const props = defineProps({
  signature: { type: Object, default: null },
  zonesPayload: { type: Object, default: null },
  storeName: { type: String, default: '' },
})

const { t } = useI18n()
const drivers = computed(() => props.signature?.drivers || [])
const zones = computed(() => props.zonesPayload?.zones || [])
const headline = computed(() => signaturePhrase(drivers.value, props.storeName))
const note = computed(() =>
  drivers.value.length
    ? t('weather.store.noteMild')
    : t('weather.store.noteNone'))
</script>

<style scoped>
.wx-card {
  background: #fff; border: 1px solid #e8ebe0; border-radius: 14px;
  padding: 18px 20px; box-shadow: 0 1px 3px rgba(20, 30, 10, 0.04);
}
.wx-sig { display: flex; gap: 16px; align-items: flex-start; }
.wx-sig-icon {
  flex: 0 0 auto; width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #eaf1ff, #f2f7ec); color: #5b7fb0; font-size: 22px;
}
.wx-sig-headline { font-weight: 650; font-size: 1.02rem; color: #2a3320; }
.wx-drivers { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0 4px; }
.wx-driver {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.82rem; padding: 3px 10px; border-radius: 999px;
  background: #f4f6ef; color: #45503a;
}
.wx-driver b { font-weight: 700; }
.wx-driver.up { color: #3f6b2f; }
.wx-driver.down { color: #9a6a1c; }
.wx-sig-note { font-size: 0.8rem; color: #8a9280; margin-top: 2px; }

.wx-divider { height: 1px; background: #eef1e8; margin: 16px 0 12px; }
.wx-zones-head {
  display: flex; justify-content: space-between; align-items: baseline;
  flex-wrap: wrap; gap: 2px 10px;
  font-weight: 650; color: #2a3320; font-size: 0.92rem; margin-bottom: 6px;
}
.wx-legend { font-size: 0.74rem; color: #9aa28e; font-weight: 500; }
.wx-legend .bi-star-fill { color: #B2C149; }

.wx-list { list-style: none; margin: 0; padding: 0; }
.wx-row {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 6px; border-bottom: 1px solid #f4f6ef;
}
.wx-row:last-child { border-bottom: none; }
.wx-row.muted { opacity: 0.6; }
.wx-row-icon {
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center; font-size: 16px;
  background: #f2f5ec; color: #6f7a5c;
}
.wx-row-icon.up { background: #eef6e8; color: #4f8f3a; }
.wx-row-icon.down { background: #fdf3e3; color: #c08a2e; }
.wx-row-main { flex: 1 1 auto; min-width: 0; }
.wx-row-top { display: flex; align-items: center; gap: 8px; }
.wx-zone { font-weight: 600; color: #2a3320; }
.wx-tag {
  font-size: 0.68rem; padding: 2px 7px; border-radius: 999px;
  background: #eef2fb; color: #4f6b9c; display: inline-flex; align-items: center; gap: 3px;
}
.wx-phrase { font-size: 0.83rem; color: #77806a; margin-top: 2px; }
.wx-row-right { flex: 0 0 auto; display: flex; align-items: center; gap: 10px; }
.wx-effect { font-weight: 700; font-size: 0.95rem; }
.wx-effect.up { color: #4f8f3a; }
.wx-effect.down { color: #c08a2e; }
.wx-band {
  font-size: 0.72rem; padding: 3px 9px; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
}
.wx-band.established { background: #eef6e8; color: #3f6b2f; }
.wx-band.watch { background: #f3f4f6; color: #6b7280; }
.wx-band.none { background: #f3f4f6; color: #9aa28e; }
.wx-empty {
  font-size: 0.84rem; color: #77806a; display: flex; align-items: flex-start; gap: 8px;
  background: #f7f9f2; border-radius: 10px; padding: 12px 14px;
}
.wx-empty .bi { color: #9aa28e; margin-top: 1px; }

@media (max-width: 720px) {
  .wx-row-right { flex-direction: column; align-items: flex-end; gap: 2px; }
}
</style>
